import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TeamView.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface TeamState {
  eventId: string;
  eventName: string;
  teamId: string;
  teamName: string;
  joinCode: string;
  startYear: number | null;
  roundId: string | null;
  roundName: string;
}

interface TeamCard {
  id: string;
  position: number;
  year: string;
  artist: string;
  title: string;
  createdAt: string;
  points?: number; // 0-4 punten per kaart
}

interface DisplayState {
  isPlaying: boolean;
  showResults: boolean;
  currentSong: {
    id: string;
    title: string;
    artist: string;
    year: number;
  } | null;
}

const TeamView: React.FC = () => {
  const { joinCode } = useParams<{ joinCode: string }>();
  const navigate = useNavigate();
  
  const [teamState, setTeamState] = useState<TeamState | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Cards state - local storage for persistence
  const [cards, setCards] = useState<TeamCard[]>([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCard, setNewCard] = useState({ year: '', artist: '', title: '' });
  const [editingCard, setEditingCard] = useState<string | null>(null);
  
  // New song modal state
  const [showNewSongModal, setShowNewSongModal] = useState(false);
  const [currentDisplayState, setCurrentDisplayState] = useState<DisplayState | null>(null);
  const lastSongIdRef = useRef<string | null>(null);
  const processedSongsRef = useRef<Set<string>>(new Set());
  const showNewSongModalRef = useRef(false);
  
  // Tab-switch detection (anti-cheat)
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showCheatWarning, setShowCheatWarning] = useState(false);
  const cheatWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    showNewSongModalRef.current = showNewSongModal;
  }, [showNewSongModal]);
  
  // Tab-switch detection (anti-cheat)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User left the tab
        setTabSwitchCount(prev => prev + 1);
      } else {
        // User came back - show warning
        setShowCheatWarning(true);
        
        // Report to server
        if (teamState) {
          fetch(`${API_BASE}/api/team-app/team/${teamState.teamId}/tab-switch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              eventId: teamState.eventId,
              count: tabSwitchCount + 1
            })
          }).catch(console.error);
        }
        
        // Auto-hide warning after 3 seconds
        if (cheatWarningTimeoutRef.current) {
          clearTimeout(cheatWarningTimeoutRef.current);
        }
        cheatWarningTimeoutRef.current = setTimeout(() => {
          setShowCheatWarning(false);
        }, 3000);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (cheatWarningTimeoutRef.current) {
        clearTimeout(cheatWarningTimeoutRef.current);
      }
    };
  }, [teamState, tabSwitchCount]);
  
  // Load cards from localStorage on mount
  useEffect(() => {
    if (joinCode) {
      const savedCards = localStorage.getItem(`hitster-cards-${joinCode}`);
      if (savedCards) {
        setCards(JSON.parse(savedCards));
      }
    }
  }, [joinCode]);
  
  // Save cards to localStorage when they change
  useEffect(() => {
    if (joinCode && cards.length > 0) {
      localStorage.setItem(`hitster-cards-${joinCode}`, JSON.stringify(cards));
    }
  }, [cards, joinCode]);
  
  // Fetch team state
  const fetchTeamState = useCallback(async () => {
    if (!joinCode) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/team-app/join/${joinCode.toUpperCase()}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTeamState(data);
        setError('');
        setLastUpdate(new Date());
        
        // If server has cards and we don't have local cards, load from server
        if (data.cards && data.cards.length > 0 && cards.length === 0) {
          setCards(data.cards);
        }
      } else if (response.status === 404) {
        setError('Team niet gevonden of team app niet ingeschakeld');
      }
    } catch (err) {
      console.error('Failed to fetch team state:', err);
    } finally {
      setLoading(false);
    }
  }, [joinCode, cards.length]);
  
  // Initial fetch and polling
  useEffect(() => {
    fetchTeamState();
    const interval = setInterval(fetchTeamState, 5000); // Poll less frequently
    return () => clearInterval(interval);
  }, [fetchTeamState]);
  
  // Poll for display state (new songs)
  useEffect(() => {
    const fetchDisplayState = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/display-state`);
        const data = await response.json();
        setCurrentDisplayState(data);
        
        // Check if a new song is playing
        if (data.isPlaying && data.currentSong?.id) {
          const songId = data.currentSong.id;
          
          // If this is a new song we haven't processed yet
          if (songId !== lastSongIdRef.current && !processedSongsRef.current.has(songId)) {
            lastSongIdRef.current = songId;
            // Open modal for new song entry
            setNewCard({ year: '', artist: '', title: '' });
            setShowNewSongModal(true);
          }
        } else if (!data.isPlaying && !data.showResults) {
          // Song stopped and not showing results - close modal, show timeline
          if (showNewSongModalRef.current) {
            // Mark current song as processed if modal was open
            if (lastSongIdRef.current) {
              processedSongsRef.current.add(lastSongIdRef.current);
            }
            setShowNewSongModal(false);
            setNewCard({ year: '', artist: '', title: '' });
          }
        }
      } catch (err) {
        console.error('Failed to fetch display state:', err);
      }
    };
    
    fetchDisplayState();
    const interval = setInterval(fetchDisplayState, 2000); // Poll more frequently for songs
    return () => clearInterval(interval);
  }, []);
  
  // Sync cards to server
  const syncCardsToServer = async (updatedCards: TeamCard[]) => {
    if (!teamState) return;
    
    try {
      await fetch(`${API_BASE}/api/team-app/team/${teamState.teamId}/cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventId: teamState.eventId,
          cards: updatedCards
        })
      });
    } catch (err) {
      console.error('Failed to sync cards:', err);
    }
  };
  
  // Add new card (with auto-sort)
  const addCard = () => {
    if (!newCard.year && !newCard.artist && !newCard.title) return;
    
    const card: TeamCard = {
      id: `card-${Date.now()}`,
      position: cards.length,
      year: newCard.year,
      artist: newCard.artist,
      title: newCard.title,
      createdAt: new Date().toISOString()
    };
    
    // Add card and auto-sort by year
    const updatedCards = [...cards, card].sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      return yearA - yearB;
    });
    updatedCards.forEach((c, i) => c.position = i);
    
    setCards(updatedCards);
    setNewCard({ year: '', artist: '', title: '' });
    setShowAddCard(false);
    syncCardsToServer(updatedCards);
  };
  
  // Add card from new song modal
  const addCardFromModal = () => {
    if (!newCard.year && !newCard.artist && !newCard.title) return;
    
    // Mark this song as processed
    if (lastSongIdRef.current) {
      processedSongsRef.current.add(lastSongIdRef.current);
    }
    
    const card: TeamCard = {
      id: `card-${Date.now()}`,
      position: cards.length,
      year: newCard.year,
      artist: newCard.artist,
      title: newCard.title,
      createdAt: new Date().toISOString()
    };
    
    // Add card and auto-sort by year
    const updatedCards = [...cards, card].sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      return yearA - yearB;
    });
    updatedCards.forEach((c, i) => c.position = i);
    
    setCards(updatedCards);
    setNewCard({ year: '', artist: '', title: '' });
    setShowNewSongModal(false);
    syncCardsToServer(updatedCards);
  };
  
  // Skip current song (don't add card)
  const skipCurrentSong = () => {
    if (lastSongIdRef.current) {
      processedSongsRef.current.add(lastSongIdRef.current);
    }
    setShowNewSongModal(false);
    setNewCard({ year: '', artist: '', title: '' });
  };
  
  // Update card
  const updateCard = (id: string, updates: Partial<TeamCard>) => {
    const updatedCards = cards.map(c => 
      c.id === id ? { ...c, ...updates } : c
    );
    setCards(updatedCards);
    syncCardsToServer(updatedCards);
  };
  
  // Delete card
  const deleteCard = (id: string) => {
    const updatedCards = cards.filter(c => c.id !== id);
    setCards(updatedCards);
    syncCardsToServer(updatedCards);
  };
  
  // Move card up/down
  const moveCard = (id: string, direction: 'up' | 'down') => {
    const index = cards.findIndex(c => c.id === id);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= cards.length) return;
    
    const updatedCards = [...cards];
    [updatedCards[index], updatedCards[newIndex]] = [updatedCards[newIndex], updatedCards[index]];
    updatedCards.forEach((c, i) => c.position = i);
    
    setCards(updatedCards);
    syncCardsToServer(updatedCards);
  };
  
  // Clear all cards
  const clearAllCards = () => {
    if (window.confirm('Weet je zeker dat je alle kaarten wilt verwijderen?')) {
      setCards([]);
      if (joinCode) {
        localStorage.removeItem(`hitster-cards-${joinCode}`);
      }
      syncCardsToServer([]);
    }
  };
  
  // Sort cards by year
  const sortCardsByYear = () => {
    const sortedCards = [...cards].sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      return yearA - yearB;
    });
    sortedCards.forEach((c, i) => c.position = i);
    setCards(sortedCards);
    syncCardsToServer(sortedCards);
  };
  
  if (loading) {
    return (
      <div className="team-view-container">
        <div className="team-view-loading">
          <div className="loading-spinner"></div>
          <p>Laden...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="team-view-container">
        <div className="team-view-error">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={() => navigate('/join')} className="back-button">
            Terug naar invoer
          </button>
        </div>
      </div>
    );
  }
  
  if (!teamState) {
    return null;
  }
  
  // Calculate left/right cards based on start year
  const startYear = teamState.startYear || 1990;
  const sortedCards = [...cards].sort((a, b) => (parseInt(a.year) || 0) - (parseInt(b.year) || 0));
  const leftCards = sortedCards.filter(c => (parseInt(c.year) || 0) <= startYear);
  const rightCards = sortedCards.filter(c => (parseInt(c.year) || 0) > startYear);
  
  // Calculate total points
  const totalPoints = cards.reduce((sum, card) => sum + (card.points || 0), 0);
  
  // Get card class based on points
  const getCardClass = (card: TeamCard) => {
    if (card.points === undefined) return '';
    if (card.points === 4) return 'all-correct';
    if (card.points > 0) return 'some-correct';
    return 'none-correct';
  };
  
  return (
    <div className="team-view-container">
      <header className="team-view-header">
        <div className="team-info">
          <h1>{teamState.teamName}</h1>
          <span className="event-name">{teamState.eventName}</span>
          {teamState.roundName && (
            <span className="round-name">{teamState.roundName}</span>
          )}
        </div>
        <div className="connection-status">
          <span className="status-dot connected"></span>
          <span className="status-text">Verbonden</span>
        </div>
      </header>
      
      <main className="team-view-main">
        {/* Visual Timeline Header */}
        <div className="visual-timeline-header">
          <span className="th-left">◀ LINKS ({leftCards.length})</span>
          <span className="th-center">Startjaar: {teamState.startYear || '----'}</span>
          <span className="th-right">RECHTS ({rightCards.length}) ▶</span>
        </div>
        
        {/* Visual Timeline Strip */}
        <div className="timeline-strip-wrapper">
          <div className="timeline-strip">
            {/* Left cards (older than start year) */}
            {leftCards.map((card) => (
              <div 
                key={card.id} 
                className={`timeline-card song-card ${getCardClass(card)}`}
                onClick={() => setEditingCard(card.id)}
              >
                <div className="card-position left">◀ LINKS</div>
                <div className="card-year">{card.year || '????'}</div>
                <div className="card-content">
                  <div className="card-title">{card.title || '???'}</div>
                  <div className="card-artist">{card.artist || '???'}</div>
                </div>
                <div className={`card-points ${card.points === 4 ? 'perfect' : (card.points || 0) > 0 ? 'partial' : 'zero'}`}>
                  {card.points !== undefined ? `${card.points} pt` : '- pt'}
                </div>
                <button className="card-delete-btn" onClick={(e) => { e.stopPropagation(); deleteCard(card.id); }}>✕</button>
              </div>
            ))}
            
            {/* Start Card */}
            <div className="timeline-card start-card">
              <span className="card-label">START</span>
              <span className="card-year">{teamState.startYear || '----'}</span>
            </div>
            
            {/* Right cards (newer than start year) */}
            {rightCards.map((card) => (
              <div 
                key={card.id} 
                className={`timeline-card song-card ${getCardClass(card)}`}
                onClick={() => setEditingCard(card.id)}
              >
                <div className="card-position right">RECHTS ▶</div>
                <div className="card-year">{card.year || '????'}</div>
                <div className="card-content">
                  <div className="card-title">{card.title || '???'}</div>
                  <div className="card-artist">{card.artist || '???'}</div>
                </div>
                <div className={`card-points ${card.points === 4 ? 'perfect' : (card.points || 0) > 0 ? 'partial' : 'zero'}`}>
                  {card.points !== undefined ? `${card.points} pt` : '- pt'}
                </div>
                <button className="card-delete-btn" onClick={(e) => { e.stopPropagation(); deleteCard(card.id); }}>✕</button>
              </div>
            ))}
          </div>
        </div>
        <div className="timeline-hint">← Scroll om alle kaartjes te zien →</div>
        
        {/* Total Points */}
        <div className="total-points-section">
          <span className="total-label">Totaal deze ronde:</span>
          <span className="total-value">{totalPoints} punten</span>
        </div>
        
        {/* Quick Add Button */}
        <div className="quick-actions">
          <button onClick={() => setShowAddCard(true)} className="add-card-btn">
            <span className="plus-icon">+</span>
            <span>Kaart toevoegen</span>
          </button>
        </div>
        
        {/* Add Card Form */}
        {showAddCard && (
          <div className="add-card-form">
            <h3>Nieuwe kaart toevoegen</h3>
            <div className="form-row">
              <div className="form-field">
                <label>Jaar</label>
                <input
                  type="number"
                  value={newCard.year}
                  onChange={(e) => setNewCard({ ...newCard, year: e.target.value })}
                  placeholder="bijv. 1985"
                  min="1900"
                  max="2030"
                  autoFocus
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Artiest</label>
                <input
                  type="text"
                  value={newCard.artist}
                  onChange={(e) => setNewCard({ ...newCard, artist: e.target.value })}
                  placeholder="bijv. Queen"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Titel</label>
                <input
                  type="text"
                  value={newCard.title}
                  onChange={(e) => setNewCard({ ...newCard, title: e.target.value })}
                  placeholder="bijv. Bohemian Rhapsody"
                />
              </div>
            </div>
            <div className="form-actions">
              <button onClick={() => setShowAddCard(false)} className="cancel-btn">
                Annuleren
              </button>
              <button onClick={addCard} className="save-btn" disabled={!newCard.year && !newCard.artist && !newCard.title}>
                Toevoegen
              </button>
            </div>
          </div>
        )}
        
        {/* Edit Card Modal */}
        {editingCard && (() => {
          const card = cards.find(c => c.id === editingCard);
          if (!card) return null;
          return (
            <div className="edit-card-modal-overlay" onClick={() => setEditingCard(null)}>
              <div className="edit-card-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Kaart bewerken</h3>
                <div className="form-field">
                  <label>Jaartal</label>
                  <input
                    type="number"
                    value={card.year}
                    onChange={(e) => updateCard(card.id, { year: e.target.value })}
                    placeholder="Jaar"
                  />
                </div>
                <div className="form-field">
                  <label>Artiest</label>
                  <input
                    type="text"
                    value={card.artist}
                    onChange={(e) => updateCard(card.id, { artist: e.target.value })}
                    placeholder="Artiest"
                  />
                </div>
                <div className="form-field">
                  <label>Titel</label>
                  <input
                    type="text"
                    value={card.title}
                    onChange={(e) => updateCard(card.id, { title: e.target.value })}
                    placeholder="Titel"
                  />
                </div>
                {card.points !== undefined && (
                  <div className="form-field">
                    <label>Punten (toegekend door spelleider)</label>
                    <div className="points-display">{card.points} punten</div>
                  </div>
                )}
                <div className="modal-actions">
                  <button onClick={() => { deleteCard(card.id); setEditingCard(null); }} className="delete-btn-modal">
                    Verwijderen
                  </button>
                  <button onClick={() => setEditingCard(null)} className="done-btn-modal">
                    Klaar
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </main>
      
      <footer className="team-view-footer">
        <span className="last-update">
          Laatste update: {lastUpdate.toLocaleTimeString()}
        </span>
        {tabSwitchCount > 0 && (
          <span className="tab-switch-count">⚠️ {tabSwitchCount}x verlaten</span>
        )}
        <span className="team-code">Code: {teamState.joinCode}</span>
      </footer>
      
      {/* New Song Modal - Opens automatically when a song plays */}
      {showNewSongModal && (
        <div className="new-song-modal-overlay">
          <div className="new-song-modal">
            <div className="modal-header">
              <h2>🎵 Nieuw Nummer!</h2>
              <p className="modal-subtitle">Vul je antwoord in</p>
            </div>
            
            {currentDisplayState?.isPlaying && (
              <div className="now-playing-indicator">
                <span className="pulse-dot"></span>
                <span>Nummer speelt nu...</span>
              </div>
            )}
            
            <div className="modal-form">
              <div className="form-field">
                <label>Jaartal</label>
                <input
                  type="number"
                  value={newCard.year}
                  onChange={(e) => setNewCard({ ...newCard, year: e.target.value })}
                  placeholder="bv. 1985"
                  autoFocus
                />
              </div>
              
              <div className="form-field">
                <label>Artiest / Band</label>
                <input
                  type="text"
                  value={newCard.artist}
                  onChange={(e) => setNewCard({ ...newCard, artist: e.target.value })}
                  placeholder="bv. Queen"
                />
              </div>
              
              <div className="form-field">
                <label>Titel</label>
                <input
                  type="text"
                  value={newCard.title}
                  onChange={(e) => setNewCard({ ...newCard, title: e.target.value })}
                  placeholder="bv. Bohemian Rhapsody"
                />
              </div>
            </div>
            
            <div className="modal-actions">
              <button onClick={skipCurrentSong} className="skip-btn">
                Overslaan
              </button>
              <button 
                onClick={addCardFromModal} 
                className="submit-btn"
                disabled={!newCard.year}
              >
                ✓ Kaart Plaatsen
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Cheat Warning Overlay */}
      {showCheatWarning && (
        <div className="cheat-warning-overlay">
          <div className="cheat-warning-modal">
            <span className="warning-icon">⚠️</span>
            <h2>Tab-wissel gedetecteerd!</h2>
            <p>De spelleider kan zien dat je de app hebt verlaten.</p>
            <p className="warning-count">Aantal keer: {tabSwitchCount}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamView;
